<template>
  <footer class="site-footer">
    <div class="site-footer__grid">
      <div class="site-footer__identity">
        <p>{{ copyrightText }}</p>
        <p class="site-footer__meta">{{ abnText }}</p>
      </div>

      <nav class="site-footer__col" :aria-label="t('footer.quickLinks')">
        <p class="site-footer__heading">{{ t('footer.quickLinks') }}</p>
        <ul>
          <li v-for="link in quickLinks" :key="link.label">
            <RouterLink v-if="link.to" :to="link.to" class="site-footer__link">{{ link.label }}</RouterLink>
            <a v-else :href="link.href" class="site-footer__link" target="_blank" rel="noopener noreferrer">
              {{ link.label }}
            </a>
          </li>
        </ul>
      </nav>

      <nav class="site-footer__col" :aria-label="t('footer.legal')">
        <p class="site-footer__heading">{{ t('footer.legal') }}</p>
        <ul>
          <li>
            <RouterLink :to="{ name: 'legal-notice' }" class="site-footer__link">{{ t('footer.legalNotice') }}</RouterLink>
          </li>
          <li>
            <RouterLink :to="{ name: 'privacy-policy' }" class="site-footer__link">{{
              t('footer.privacyPolicy')
            }}</RouterLink>
          </li>
          <li>
            <RouterLink :to="{ name: 'terms-of-use' }" class="site-footer__link">{{ t('footer.termsOfUse') }}</RouterLink>
          </li>
        </ul>
      </nav>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { EDUCATION_SITE_URL, MAIN_SITE_URL } from '../../utils/educationHost'

const props = defineProps({
  isMobile: { type: Boolean, default: false },
  site: { type: String, default: 'main' },
  copyright: { type: String, default: '' },
  abn: { type: String, default: '' },
})

const { t } = useI18n()

const copyrightText = computed(() => props.copyright || t('footer.copyright'))
const abnText = computed(() => props.abn || t('footer.abn'))

const quickLinks = computed(() => {
  const onEducationSite = props.site === 'education'
  return [
    onEducationSite
      ? { label: t('footer.immigrationSite'), href: `${MAIN_SITE_URL}/cn` }
      : { label: t('footer.immigrationSite'), to: { name: 'home' } },
    onEducationSite
      ? { label: t('footer.educationSite'), to: { name: 'education-home' } }
      : { label: t('footer.educationSite'), href: `${EDUCATION_SITE_URL}/` },
  ]
})
</script>

<style lang="scss" scoped>
.site-footer {
  width: 100%;
  background: #333333;
  color: #ffffff;
}

.site-footer__grid {
  box-sizing: border-box;
  width: 100%;
  padding: 2.25rem 10% 2rem;
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(10rem, 1fr) minmax(10rem, 1fr);
  gap: 2rem 3rem;
  align-items: start;
}

.site-footer__identity {
  p {
    margin: 0;
    line-height: 1.6;
  }
}

.site-footer__meta {
  margin-top: 0.35rem;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.92rem;
}

.site-footer__heading {
  margin: 0 0 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
}

.site-footer__col ul {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.site-footer__link {
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-size: 0.95rem;
  line-height: 1.4;

  &.router-link-active {
    text-decoration: none;
  }

  &:hover,
  &:focus-visible {
    color: #ffffff;
    text-decoration: underline;
  }
}

@media (max-width: 768px) {
  .site-footer__grid {
    grid-template-columns: 1fr;
    padding: 1.75rem 1.5rem 2rem;
    gap: 1.5rem;
  }
}
</style>
